import React from 'react';
import { JobSource, FormErrors } from '../types';

interface BasicInfoSectionProps {
  formData: JobSource;
  setFormData: React.Dispatch<React.SetStateAction<JobSource>>;
  errors: FormErrors;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  formData,
  setFormData,
  errors,
}) => {
  const handleChange = (field: keyof JobSource, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="tab-pane active">
      <div className="row">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="name" className="form-label fw-semibold">
              名前 <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="name"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
            {errors.name && (
              <div className="invalid-feedback">{errors.name.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="official_name" className="form-label fw-semibold">
              公式名称
            </label>
            <input
              type="text"
              id="official_name"
              className={`form-control ${errors.official_name ? 'is-invalid' : ''}`}
              value={formData.official_name || ''}
              onChange={(e) => handleChange('official_name', e.target.value)}
            />
            <small className="form-text text-muted">公式名称（表示用）</small>
            {errors.official_name && (
              <div className="invalid-feedback">{errors.official_name.join(', ')}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="url" className="form-label fw-semibold">
              URL <span className="text-danger">*</span>
            </label>
            <input
              type="url"
              id="url"
              className={`form-control ${errors.url ? 'is-invalid' : ''}`}
              value={formData.url || ''}
              onChange={(e) => handleChange('url', e.target.value)}
              required
            />
            {errors.url && (
              <div className="invalid-feedback">{errors.url.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="affiliate_link" className="form-label fw-semibold">
              アフィリエイトリンク
            </label>
            <input
              type="url"
              id="affiliate_link"
              className={`form-control ${errors.affiliate_link ? 'is-invalid' : ''}`}
              value={formData.affiliate_link || ''}
              onChange={(e) => handleChange('affiliate_link', e.target.value)}
            />
            <small className="form-text text-muted">アフィリエイトリンク（任意）</small>
            {errors.affiliate_link && (
              <div className="invalid-feedback">{errors.affiliate_link.join(', ')}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="official_site_url" className="form-label fw-semibold">
              公式サイトURL
            </label>
            <input
              type="url"
              id="official_site_url"
              className={`form-control ${errors.official_site_url ? 'is-invalid' : ''}`}
              value={formData.official_site_url || ''}
              onChange={(e) => handleChange('official_site_url', e.target.value)}
            />
            {errors.official_site_url && (
              <div className="invalid-feedback">{errors.official_site_url.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label htmlFor="registration_form_url" className="form-label fw-semibold">
              応募完了フォームURL
            </label>
            <input
              type="url"
              id="registration_form_url"
              className={`form-control ${errors.registration_form_url ? 'is-invalid' : ''}`}
              value={formData.registration_form_url || ''}
              onChange={(e) => handleChange('registration_form_url', e.target.value)}
              placeholder="応募完了後に申し込みが必要なURL"
            />
            {errors.registration_form_url && (
              <div className="invalid-feedback">{errors.registration_form_url.join(', ')}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="form-group mb-3">
            <label htmlFor="display_priority" className="form-label fw-semibold">
              表示優先度
            </label>
            <input
              type="number"
              id="display_priority"
              className={`form-control ${errors.display_priority ? 'is-invalid' : ''}`}
              value={formData.display_priority || 1}
              onChange={(e) => handleChange('display_priority', parseInt(e.target.value, 10))}
              min={1}
              step={1}
            />
            <small className="form-text text-muted">数字が小さいほど優先度が高い（1が最優先）</small>
            {errors.display_priority && (
              <div className="invalid-feedback">{errors.display_priority.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group mb-3">
            <label htmlFor="partnership_type" className="form-label fw-semibold">
              提携タイプ
            </label>
            <select
              id="partnership_type"
              className={`form-select ${errors.partnership_type ? 'is-invalid' : ''}`}
              value={formData.partnership_type || 'none'}
              onChange={(e) => handleChange('partnership_type', e.target.value as JobSource['partnership_type'])}
            >
              <option value="none">データのみ</option>
              <option value="direct">直接提携</option>
              <option value="affiliate">アフィリエイト</option>
            </select>
            {errors.partnership_type && (
              <div className="invalid-feedback">{errors.partnership_type.join(', ')}</div>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group mb-3">
            <label htmlFor="public_status" className="form-label fw-semibold">
              公開ステータス
            </label>
            <select
              id="public_status"
              className={`form-select ${errors.public_status ? 'is-invalid' : ''}`}
              value={formData.public_status || 'private'}
              onChange={(e) => handleChange('public_status', e.target.value as JobSource['public_status'])}
            >
              <option value="private">非公開</option>
              <option value="public">公開</option>
            </select>
            {errors.public_status && (
              <div className="invalid-feedback">{errors.public_status.join(', ')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;



